import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight, Camera } from "lucide-react";

// Drop original project photos into /public/images/projects/ using the filenames below —
// they take over automatically, with the stock URLs as fallback until then.
const PROJECT_ASSETS = "/images/projects";

const TRANSFORMATIONS = [
  {
    id: "kdr",
    title: "Knock-Down & Rebuild",
    desc: "Original post-war dwelling transformed into a modern architectural family home.",
    color: "#D500F9",
    localBefore: "kdr-before.jpg",
    localAfter: "kdr-after.jpg",
    before: "https://images.pexels.com/photos/1029599/pexels-photo-1029599.jpeg?auto=compress&cs=tinysrgb&w=1200",
    after: "https://images.pexels.com/photos/32870/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1200",
    beforeAlt: "Original older dwelling before knock-down",
    afterAlt: "Completed modern architectural home after rebuild",
  },
  {
    id: "childcare",
    title: "Childcare Centre Asset",
    desc: "Undeveloped greenfield site delivered as a turnkey, state-of-the-art early-learning facility.",
    color: "#FF1744",
    localBefore: "childcare-before.jpg",
    localAfter: "childcare-after.jpg",
    before: "https://images.pexels.com/photos/440731/pexels-photo-440731.jpeg?auto=compress&cs=tinysrgb&w=1200",
    after: "https://images.pexels.com/photos/3662667/pexels-photo-3662667.jpeg?auto=compress&cs=tinysrgb&w=1200",
    beforeAlt: "Regional NSW childcare site — slab preparation and ground drainage underway",
    afterAlt: "Regional NSW childcare centre — precision LGS steel frame erected",
    badge: "Specialized Commercial & Childcare Assets",
  },
  {
    id: "commercial",
    title: "Commercial / Shop Fit-Out",
    desc: "Bare construction shell fitted out and trading — latest delivery: Monstone Commercial Kitchen & Juice Bar.",
    color: "#00B0FF",
    localBefore: "commercial-before.jpg",
    localAfter: "commercial-after.jpg",
    before: "https://images.pexels.com/photos/2119712/pexels-photo-2119712.jpeg?auto=compress&cs=tinysrgb&w=1200",
    after: "https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=1200",
    beforeAlt: "Bare construction shell before fit-out",
    afterAlt: "Premium glass commercial facility after fit-out",
    caption: "Monstone Commercial Hospitality Fit-Out — Completed",
  },
];

const ProjectImg = ({ local, fallback, alt }) => {
  const [src, setSrc] = useState(`${PROJECT_ASSETS}/${local}`);
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      draggable={false}
      onError={() => src !== fallback && setSrc(fallback)}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
};

const BeforeAfter = ({ item }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-[4/3] overflow-hidden select-none" data-testid={`ba-slider-${item.id}`}>
      <ProjectImg local={item.localBefore} fallback={item.before} alt={item.beforeAlt} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <ProjectImg local={item.localAfter} fallback={item.after} alt={item.afterAlt} />
      </div>

      <span className="absolute top-4 left-4 rounded-full glass border border-white/20 px-3 py-1 font-display text-[10px] font-bold tracking-[0.2em] text-white/90 pointer-events-none">
        BEFORE
      </span>
      {item.badge && (
        <span
          className="absolute top-14 right-4 rounded-full glass border border-white/15 px-3 py-1 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none"
          data-testid={`ba-badge-${item.id}`}
        >
          {item.badge}
        </span>
      )}
      {item.caption && (
        <span
          className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-[85%] rounded-full glass border border-white/15 px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none text-center sm:text-left"
          data-testid={`ba-caption-${item.id}`}
        >
          {item.caption}
        </span>
      )}
      <span
        className="absolute top-4 right-4 rounded-full px-3 py-1 font-display text-[10px] font-bold tracking-[0.2em] text-white pointer-events-none"
        style={{ background: `${item.color}CC`, boxShadow: `0 0 16px ${item.color}66` }}
      >
        AFTER
      </span>

      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/90 pointer-events-none"
        style={{ left: `${pos}%`, boxShadow: `0 0 20px ${item.color}` }}
      >
        <span
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full glass border-2 flex items-center justify-center text-white"
          style={{ borderColor: item.color, boxShadow: `0 0 24px ${item.color}88` }}
        >
          <ChevronsLeftRight size={18} />
        </span>
      </div>

      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label={`Before and after comparison for ${item.title}`}
        data-testid={`ba-range-${item.id}`}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize m-0"
      />
    </div>
  );
};

export const BeforeAfterGallery = () => (
  <section id="gallery" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 border-t border-white/10" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14"
      >
        <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FF1744] mb-4 flex items-center gap-3">
          <Camera size={14} /> SIGNATURE WORK
        </p>
        <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
          Transformations &amp;{" "}
          <em className="font-serif-lux italic font-semibold text-gradient-fire">Delivered Builds.</em>
        </h2>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-white/60 leading-relaxed">
          Drag the handle on each build — from bare earth and tired dwellings to turnkey,
          income-ready NSW assets.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TRANSFORMATIONS.map((item, i) => (
          <motion.article
            key={item.id}
            data-testid={`gallery-card-${item.id}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.12 }}
            className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
          >
            <BeforeAfter item={item} />
            <div className="p-6 border-t-2" style={{ borderTopColor: item.color }}>
              <h3 className="font-display font-bold text-lg text-white tracking-tight">{item.title}</h3>
              <p className="mt-2 text-xs text-white/55 leading-relaxed">{item.desc}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
